/**
 * 活動履歴・活動メール・カレンダーの型と入力検証（02 5.3.8 activity・5.3.9 calendar）。
 * - 活動の一覧は期間（activity_at_from／to。最大 366 日）が必須。case_code か customer_code があれば任意（D-114）。
 *   期間の組み合わせの検査は、どの条件があるかで変わるため service で行う（400 VALIDATION_001）
 * - カレンダーの期間は from〜to が最大 62 日（D-045）
 */
import { z } from 'zod';
import { ACTIVITY_DIRECTION_TYPE_VALUES } from '../enums/activity-direction-type';
import { ACTIVITY_TYPE_VALUES } from '../enums/activity-type';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { SALES_PHASE_TYPE_VALUES } from '../enums/sales-phase-type';
import { zCode, zDate, zDateTime, zEnum, zRequiredText, zShortText, zSystemCode, zVersion } from './common.schema';
import { zNullable } from './customer.schema';
import { CaseItemSchema, SALES_REFERENCE_SHAPE, zRequiredCode } from './case.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 活動の一覧の期間の上限（D-011） */
export const ACTIVITY_PERIOD_MAX_DAYS = 366;
/** カレンダーの期間の上限（D-045） */
export const CALENDAR_PERIOD_MAX_DAYS = 62;

const ACTIVITY_DETAIL_SHAPE = {
  activity_result: zShortText(2000).optional(),
  reason: zShortText(2000).optional(),
  materials: zShortText(2000).optional(),
  learning: zShortText(2000).optional(),
};

const ACTIVITY_INPUT_SHAPE = {
  ...SALES_REFERENCE_SHAPE,
  case_code: zNullable(zCode).optional(),
  activity_at: zDateTime,
  activity_type: zEnum(ACTIVITY_TYPE_VALUES),
  activity_direction_type: zNullable(zEnum(ACTIVITY_DIRECTION_TYPE_VALUES)).optional(),
  sales_phase_type: zNullable(zEnum(SALES_PHASE_TYPE_VALUES)).optional(),
  subject: zRequiredText(500, 'MSG-V-056'),
  activity_content: zShortText(10000).optional(),
  ...ACTIVITY_DETAIL_SHAPE,
};

/** API-ACTV-02 活動履歴の登録 */
export const ActivityCreateSchema = z.object(ACTIVITY_INPUT_SHAPE).strict();

/** API-ACTV-04 活動履歴の更新（すべて任意＋version） */
export const ActivityUpdateSchema = z
  .object({
    ...ACTIVITY_INPUT_SHAPE,
    customer_code: ACTIVITY_INPUT_SHAPE.customer_code.optional(),
    activity_at: ACTIVITY_INPUT_SHAPE.activity_at.optional(),
    activity_type: ACTIVITY_INPUT_SHAPE.activity_type.optional(),
    subject: ACTIVITY_INPUT_SHAPE.subject.optional(),
    version: zVersion,
  })
  .strict();

/** API-ACTV-07 メール送信をともなう活動記録（宛先は企業担当者。送信は worker） */
export const ActivityEmailCreateSchema = z
  .object({
    customer_code: zRequiredCode('MSG-V-059'),
    case_code: zNullable(zCode).optional(),
    customer_contact_code: z.string({ message: 'MSG-E-092' }).uuid('MSG-E-092'),
    sales_user_code: zSystemCode.optional(),
    sales_phase_type: zNullable(zEnum(SALES_PHASE_TYPE_VALUES)).optional(),
    subject: zRequiredText(500, 'MSG-V-056'),
    activity_content: zRequiredText(10000, 'MSG-V-091'),
    ...ACTIVITY_DETAIL_SHAPE,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const EMAIL_SEND_STATUS_VALUES = ['queued', 'sent'] as const;

export const ActivitySchema = z
  .object({
    activity_code: z.string(),
    customer_code: z.string(),
    customer_name: z.string().nullable(),
    customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    case_code: z.string().nullable(),
    case_name: z.string().nullable(),
    case_deleted: z.boolean(),
    customer_contact_code: z.string().nullable(),
    customer_contact_name: z.string().nullable(),
    sales_user_code: z.string(),
    sales_user_name: z.string().nullable(),
    activity_at: z.string(),
    activity_type: z.enum(ACTIVITY_TYPE_VALUES),
    activity_direction_type: z.enum(ACTIVITY_DIRECTION_TYPE_VALUES).nullable(),
    sales_phase_type: z.enum(SALES_PHASE_TYPE_VALUES).nullable(),
    subject: z.string(),
    activity_content: z.string().nullable(),
    activity_result: z.string().nullable(),
    reason: z.string().nullable(),
    materials: z.string().nullable(),
    learning: z.string().nullable(),
    sent_email_address: z.string().nullable(),
    email_sent_at: z.string().nullable(),
    sent_email_message_id: z.string().nullable(),
    email_send_status: z.enum(EMAIL_SEND_STATUS_VALUES).nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-ACTV-03 の 1 件（案件があれば商品の一覧を付ける：SCR-019） */
export const ActivityDetailSchema = ActivitySchema.extend({ case_items: z.array(CaseItemSchema) });

export const ACTIVITY_LIST_SORT_VALUES = ['activity_at', 'created_at'] as const;

/** API-ACTV-01（02 2.3.2） */
export const ActivityListQuerySchema = buildListQuerySchema(ACTIVITY_LIST_SORT_VALUES, '-activity_at').extend({
  activity_at_from: zDateTime.optional(),
  activity_at_to: zDateTime.optional(),
  customer_code: zCode.optional(),
  case_code: zCode.optional(),
  sales_user_code: zSystemCode.optional(),
  activity_type: zEnum(ACTIVITY_TYPE_VALUES).optional(),
});

// ───────── カレンダー（API-CAL-01） ─────────

export const CALENDAR_EVENT_TYPE_VALUES = ['activity', 'caseDueDate'] as const;

export const CalendarEventQuerySchema = z.object({
  from: zDate,
  to: zDate,
  sales_user_code: zSystemCode.optional(),
  /** `activity`・`caseDueDate` のカンマ区切り（既定は両方） */
  event_types: z
    .string()
    .trim()
    .optional()
    .transform((value, context) => {
      if (value === undefined || value === '') {
        return [...CALENDAR_EVENT_TYPE_VALUES];
      }
      const types = [...new Set(value.split(',').map((item) => item.trim()))];
      if (!types.every((type) => (CALENDAR_EVENT_TYPE_VALUES as readonly string[]).includes(type))) {
        context.addIssue({ code: 'custom', message: 'MSG-V-020' });
        return z.NEVER;
      }
      return types as Array<(typeof CALENDAR_EVENT_TYPE_VALUES)[number]>;
    }),
});

export const CalendarEventSchema = z.object({
  event_type: z.enum(CALENDAR_EVENT_TYPE_VALUES),
  event_code: z.string(),
  title: z.string(),
  start_at: z.string().nullable(),
  event_date: z.string(),
  is_all_day: z.boolean(),
  activity_type: z.enum(ACTIVITY_TYPE_VALUES).nullable(),
  case_status: z.string().nullable(),
  case_status_name: z.string().nullable(),
  customer_code: z.string(),
  customer_name: z.string().nullable(),
  case_code: z.string().nullable(),
  case_name: z.string().nullable(),
  sales_user_code: z.string(),
  sales_user_name: z.string().nullable(),
});

export type ActivityCreateInput = z.infer<typeof ActivityCreateSchema>;
export type ActivityUpdateInput = z.infer<typeof ActivityUpdateSchema>;
export type ActivityEmailCreateInput = z.infer<typeof ActivityEmailCreateSchema>;
export type Activity = z.infer<typeof ActivitySchema>;
export type ActivityDetail = z.infer<typeof ActivityDetailSchema>;
export type ActivityListQuery = z.infer<typeof ActivityListQuerySchema>;
export type CalendarEventQuery = z.infer<typeof CalendarEventQuerySchema>;
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPE_VALUES)[number];
export type EmailSendStatus = (typeof EMAIL_SEND_STATUS_VALUES)[number];
